import React, { useState, useEffect } from "react";

const ViewResumes = ({ roles, fetchResumes, onDeleteResume, onDeleteAllResumes }) => {
  const [selectedRole, setSelectedRole] = useState("");
  const [resumeList, setResumeList] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (selectedRole) {
      loadInitialResumes();
    } else {
      setResumeList([]);
      setPagination(null);
    }
  }, [selectedRole]);

  const loadInitialResumes = async () => {
    setLoading(true);
    try {
      const result = await fetchResumes(selectedRole, 1, 10);
      setResumeList(result.resumes);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading resumes:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreResumes = async () => {
    if (!pagination || !pagination.has_more) return;
    
    setLoadingMore(true);
    try {
      const nextPage = pagination.current_page + 1;
      const result = await fetchResumes(selectedRole, nextPage, 10);
      setResumeList(prev => [...prev, ...result.resumes]);
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading more resumes:", error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDeleteResume = async (resume) => {
    const fileName = resume.file_name || `${resume.id}.pdf`; // Use actual filename or fallback to .pdf
    const success = await onDeleteResume(selectedRole, fileName);
    if (success) {
      // Remove the deleted resume from the list
      setResumeList(prev => prev.filter(r => r.id !== resume.id));
      // Update pagination count
      if (pagination) {
        setPagination(prev => ({
          ...prev,
          total_resumes: prev.total_resumes - 1
        }));
      }
    }
  };

  const handleDeleteAllResumes = async () => {
    const success = await onDeleteAllResumes(selectedRole);
    if (success) {
      setResumeList([]);
      setPagination(null);
    }
  };

  return (
    <div className="flex justify-center w-full p-10">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-md p-8 border border-gray-200">

        <h2 className="text-2xl font-semibold text-gray-900 mb-4">View Resumes</h2>

        <label className="text-gray-700 font-medium block mb-2">Select Role</label>
        <select
          className="w-full border border-gray-300 rounded-lg p-3 mb-6"
          value={selectedRole}
          onChange={(e) => setSelectedRole(e.target.value)}
        >
          <option value="">Choose Role</option>
          {roles.map((r) => (
            <option key={r} value={r}>{r.slice(0, -1)}</option>
          ))}
        </select>

        {/* DELETE ALL BUTTON */}
        {selectedRole && resumeList.length > 0 && (
          <div className="mb-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {pagination && `Total: ${pagination.total_resumes} resumes`}
            </div>
            <button
              onClick={handleDeleteAllResumes}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
            >
              Delete All Resumes
            </button>
          </div>
        )}

        {/* LOADING STATE */}
        {loading && (
          <div className="text-center py-8">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <p className="text-gray-600 mt-2">Loading resumes...</p>
          </div>
        )}

        {/* SCROLLABLE RESUME LIST */}
        {!loading && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-2">
            {resumeList.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No resumes found</p>
            ) : (
              resumeList.map((resume, idx) => (
                <div
                  key={idx}
                  className="px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 flex justify-between items-start"
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800">{resume.id}</p>
                      {resume.file_extension && (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          {resume.file_extension.toUpperCase().replace('.', '')}
                        </span>
                      )}
                    </div>
                    <p className="text-gray-600 text-sm mt-1">
                      {resume.resume_text.slice(0, 200)}...
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteResume(resume)}
                    className="ml-4 bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              ))
            )}
          </div>
        )}

        {/* LOAD MORE BUTTON */}
        {!loading && pagination && pagination.has_more && (
          <div className="mt-6 text-center">
            <button
              onClick={loadMoreResumes}
              disabled={loadingMore}
              className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-2 rounded-lg font-medium transition-colors"
            >
              {loadingMore ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Loading...
                </>
              ) : (
                `Load More (${pagination.total_resumes - resumeList.length} remaining)`
              )}
            </button>
          </div>
        )}
        
      </div>
    </div>
  );
};

export default ViewResumes;
